import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Alarm, DashboardSummary, Machine } from '@bridgewerk/shared';
import {
  getOrCreateWsClient,
  releaseWsClient,
  type WsClient,
  type WsClientDeps,
  type WsConnectionState,
} from '../lib/ws-client.js';
import { dashboardKeys, machineKeys } from '../lib/query-keys.js';
import { useToast } from '../components/ui/Toast.js';

export interface UseShopLiveDataOptions {
  readonly shopId: string | null;
  readonly baseUrl: string;
  readonly getToken: () => Promise<string | null> | string | null;
  /** Test seam — defaults to the singleton registry. */
  readonly createClient?: (deps: WsClientDeps) => WsClient;
  /** Test seam — defaults to the singleton registry. */
  readonly disposeClient?: (shopId: string) => void;
}

export interface UseShopLiveDataResult {
  readonly connection: WsConnectionState;
}


export function useShopLiveData(opts: UseShopLiveDataOptions): UseShopLiveDataResult {
  const { shopId, baseUrl, getToken, createClient, disposeClient } = opts;
  const queryClient = useQueryClient();
  const toast = useToast();
  const [connection, setConnection] = useState<WsConnectionState>('connecting');

  useEffect(() => {
    if (!shopId) return;
    const factory = createClient ?? getOrCreateWsClient;
    const dispose = disposeClient ?? releaseWsClient;
    const client = factory({ shopId, baseUrl, getToken });
    setConnection(client.state);

    const offState = client.on('state', (state) => {
      setConnection(state);
      if (state === 'failed') {
        toast.push({
          key: 'ws-failed',
          tone: 'warning',
          title: 'Live updates unavailable',
          description: 'Sign out and back in if the issue persists.',
        });
      }
    });

    const offSnapshot = client.on('snapshot', (event) => {
      queryClient.setQueryData(machineKeys.list(shopId), event.payload.machines);
    });

    const offUpdate = client.on('machine_update', (event) => {
      const updated = event.payload.machine;
      queryClient.setQueryData<Machine[]>(machineKeys.list(shopId), (old) => {
        if (!old) return [updated];
        const idx = old.findIndex((m) => m.machine_id === updated.machine_id);
        if (idx < 0) return [...old, updated];
        const next = old.slice();
        next[idx] = updated;
        return next;
      });
      queryClient.setQueryData<Machine>(
        machineKeys.detail(shopId, updated.machine_id),
        updated,
      );
    });

    const offAlarm = client.on('alarm', (event) => {
      const { machineId, alarm } = event.payload;
      queryClient.setQueryData<Alarm[]>(machineKeys.alarms(shopId, machineId), (old) => {
        if (!old) return [alarm];
        const exists = old.some(
          (a) => a.alarm_code === alarm.alarm_code && a.first_seen_at === alarm.first_seen_at,
        );
        if (exists) return old;
        return [alarm, ...old];
      });
      queryClient.setQueryData<DashboardSummary>(
        dashboardKeys.stats(shopId),
        (old) =>
          old
            ? { ...old, active_alarm_count: old.active_alarm_count + 1, alarm_count: old.alarm_count + 1 }
            : old,
      );
      toast.push({
        key: `alarm-${machineId}-${alarm.alarm_code}-${alarm.first_seen_at}`,
        tone: 'critical',
        title: `Alarm ${alarm.alarm_code}`,
        description: `${machineId}${alarm.program_name ? ` · ${alarm.program_name}` : ''}`,
      });
    });

    return () => {
      offState();
      offSnapshot();
      offUpdate();
      offAlarm();
      dispose(shopId);
    };
  }, [shopId, baseUrl, getToken, createClient, disposeClient, queryClient, toast]);

  // Fallback poll disabled — re-enable when WebSocket API Gateway is wired up.

  return { connection };
}
